import React, { useState } from 'react';
import { Mail, MapPin, Clock, ArrowRight, Facebook, Instagram, Twitter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { IMAGES } from '../mock/mock';
import { submitLead } from '../services/api';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', goal: '', message: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Please add your name and email'); return; }
    setLoading(true);
    try {
      await submitLead({ ...form, source: 'contact-page' });
      toast.success("Thanks! Sam will be in touch shortly.");
      setForm({ name: '', email: '', phone: '', goal: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };
  return (
    <main>
      <section className="relative overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img src={IMAGES.pt2} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/70 to-black" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
          <div className="text-[11px] tracking-[0.3em] uppercase text-brand">Let's Do This</div>
          <h1 className="mt-4 font-display italic text-white uppercase text-6xl md:text-8xl leading-[0.9]">Get In <span className="text-brand">Touch</span></h1>
          <p className="mt-5 max-w-2xl text-white/70">Your journey starts here. Drop your details and we'll reply within 24 hours with the best next step for you.</p>
        </div>
      </section>

      <section className="py-20 bg-[#0a0a0d]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: MapPin, label: 'Studio', value: 'ST Fitness, inside Lycan Strength, Unit 2, Oasis Business Centre, Irlam, Manchester M44 5BL' },
              { icon: Mail, label: 'Email', value: 'sam.turner@st-fitness-coach.co.uk' },
              { icon: Clock, label: 'Hours', value: 'Monday to Friday' },
            ].map((item, i) => (
              <Card key={i} className="reveal bg-[#111116] border-white/5 rounded-none p-5 flex items-start gap-4">
                <div className="w-11 h-11 grid place-items-center brand-gradient text-white flex-shrink-0"><item.icon className="w-5 h-5" /></div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">{item.label}</div>
                  <div className="text-white mt-1">{item.value}</div>
                </div>
              </Card>
            ))}
            <div className="reveal bg-[#111116] border border-white/5 p-5">
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">Follow</div>
              <div className="flex items-center gap-3 mt-3">
                {[Facebook, Twitter, Instagram].map((Icon, i) => (
                  <a key={i} href="#" className="w-10 h-10 grid place-items-center border border-white/15 hover:border-brand hover:text-brand transition-colors">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 reveal bg-[#111116] border border-white/5 p-6 md:p-10 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Email</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Telephone</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Your Goal</label>
              <Select value={form.goal} onValueChange={(v) => setForm({ ...form, goal: v })}>
                <SelectTrigger className="mt-1 bg-transparent border-white/10 rounded-none text-white">
                  <SelectValue placeholder="Select a goal" />
                </SelectTrigger>
                <SelectContent className="bg-[#111116] text-white border-white/10">
                  <SelectItem value="lose-weight">Lose weight</SelectItem>
                  <SelectItem value="build-strength">Build strength</SelectItem>
                  <SelectItem value="postnatal">Postnatal recovery</SelectItem>
                  <SelectItem value="online">Online coaching</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Message</label>
              <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
            </div>
            <Button type="submit" disabled={loading} className="brand-gradient w-full text-white uppercase tracking-widest text-xs rounded-none py-6 glow-brand">
              {loading ? 'Sending...' : (<>Submit <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
            <p className="text-white/40 text-xs">By submitting you agree to be contacted about your enquiry. We never share your details.</p>
          </form>
        </div>
      </section>
    </main>
  );
}
